import { useEffect, useRef } from 'react';
import { useMedia } from 'react-use';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import twMerge from 'twMerge';

import Section from 'molecules/Section';

import nextArrowDark from 'components/Webinars/assets/next-arrow-dark.svg';
import nextArrowLight from 'components/Webinars/assets/next-arrow-light.svg';
import prevArrowDark from 'components/Webinars/assets/prev-arrow-dark.svg';
import prevArrowLight from 'components/Webinars/assets/prev-arrow-light.svg';
import {
  headshotStyles,
  nextArrowStyles,
  prevArrowStyles,
  slideContainer,
  slideInnerContainer,
  titleStyles,
} from 'components/Webinars/styles/SpeakerCarousel.styles';

import richTextParser from 'utils/richTextParser/richTextParser';

import screens from 'theme/spacing/screens';

import type { ContentfulComponentCard } from 'graphqlTypes';
import type { FC } from 'react';

interface CardProps extends ContentfulComponentCard {
  centerText?: boolean;
}

const SpeakersCarousel: FC<CardProps> = ({ activeIndex, speakers }) => {
  const swiperRef = useRef();
  const isMobile = useMedia(`(max-width: ${screens.xl})`, false);

  useEffect(() => {
    if (activeIndex > -1) {
      swiperRef?.current?.slideTo(activeIndex);
    }
  }, [activeIndex]);

  return (
    <Section containerClass="relative !pb-0 md:!pb-20 !px-0 md:!px-8" padding="small" id="speakers">
      {speakers?.length > 1 && (
        <>
          <img
            role="presentation"
            alt="next-arrow"
            src={isMobile ? nextArrowLight : nextArrowDark}
            className={twMerge(nextArrowStyles())}
            onClick={() => swiperRef.current?.slideNext()}
            data-testid={isMobile ? 'light-next-arrow' : 'dark-next-arrow'}
          />
          <img
            role="presentation"
            alt="prev-arrow"
            src={isMobile ? prevArrowLight : prevArrowDark}
            className={twMerge(prevArrowStyles())}
            onClick={() => swiperRef.current?.slidePrev()}
            data-testid={isMobile ? 'light-prev-arrow' : 'dark-prev-arrow'}
          />
        </>
      )}
      <h3 className={twMerge(titleStyles())}>Meet your speaker{speakers?.length > 1 ? 's' : ''}</h3>
      <Swiper
        pagination={{ clickable: true }}
        modules={[Navigation, Pagination]}
        loop={true}
        spaceBetween={0}
        className="mySwiper"
        onBeforeInit={swiper => {
          swiperRef.current = swiper;
        }}
      >
        {speakers?.map(({ bio, company, fullName, position, headshot }, index) => (
          <SwiperSlide className="speaker-slide" key={index}>
            <div className={twMerge(slideContainer())}>
              <div className={twMerge(slideInnerContainer())}>
                {headshot && <img src={headshot.file.url} className={twMerge(headshotStyles())} alt="headshot" />}
                <div className="speaker-bio flex flex-1 flex-col">
                  {fullName && (
                    <p className={`text-xl font-bold text-white ${position || company ? '' : 'mb-5'}`}>{fullName}</p>
                  )}
                  {(position || company) && (
                    <p className="mb-5 text-lg font-extralight text-white">
                      {position}
                      {position && company ? ', ' : ''}
                      {company}
                    </p>
                  )}
                  <div className="text-md text-white">{richTextParser(bio, null, 'dark' as RichText)}</div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </Section>
  );
};

export default SpeakersCarousel;
